import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import styled from 'styled-components';
import PieChartIcon from '@mui/icons-material/PieChart';

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px;
  /* background-color: rgb(40, 47, 60); */
  background-color: #17163b;
  border-radius: 2px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
  height: auto;
  font-family: 'Quicksand', sans-serif;
`;

const ChartTitleStyled = styled.h2`
  margin-bottom: 20px;
  font-size: 16px;
  color: white;
  font-weight: 400;
  text-align: left;
  width: 100%;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: normal;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ChartContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 330px;
  max-width: 800px;
  max-height: 600px;
  position: relative;
  overflow: hidden;
`;

const Tooltip = styled.div`
  position: absolute;
  background: #333;
  color: #fff;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.3s;
`;

const LegendTooltip = styled.div`
  position: fixed;
  background: #333;
  color: #fff;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: 1000;
`;

const MarketSharePieChart = ({ data, keyword, location }) => {
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const legendTooltipRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0) {
      console.error('No data provided or data is empty.');
      return;
    }

    const svg = d3.select(svgRef.current);
    const containerWidth = svgRef.current.parentNode.clientWidth;
    const containerHeight = svgRef.current.parentNode.clientHeight || 600;

    // Define dimensions and radius
    const width = containerWidth;
    const height = containerHeight;
    const radius = Math.min(width, height) / 2 - 20;

    svg.attr('width', width).attr('height', height);

    // Clear previous contents
    svg.selectAll('*').remove();

    // Create a group element to center the pie chart
    const g = svg.append('g')
      .attr('transform', `translate(${radius + 20},${height / 2})`);

    // Format and sort data, select top 10 by rank
    const formattedData = data
      .filter(d => d.rank !== "na" && !isNaN(+d.rank)) // Filter out 'na' ranks
      .map(d => ({
        title: d.title,
        et: +d.et.replace(/,/g, ''),
        rank: +d.rank, // Ensure rank is numeric
        url: d.url
      }))
      .sort((a, b) => a.rank - b.rank) // Sort by rank in ascending order
      .slice(0, 10); // Take top 10 by rank

    // Format backlinks with commas
    const formatBacklinks = d3.format(',');

    // Generate unique vibrant colors
    const generateUniqueColors = (data) => {
      const colorScale = d3.scaleSequential(d3.interpolateCool); // Use a sequential color scale
      
      return data.map((_, i) => colorScale(i / data.length)); // Generate a color based on the index
    };

    const uniqueColors = generateUniqueColors(formattedData);

    // Define pie chart layout
    const pie = d3.pie()
      .value(d => d.et)
      .sort(null);

    // Define arc generator
    const arc = d3.arc()
      .innerRadius(0)
      .outerRadius(radius);

    // Define pie chart slices
    g.selectAll('path')
  .data(pie(formattedData))
  .enter().append('path')
  .attr('d', arc)
  .attr('fill', (d, i) => uniqueColors[i]) // Use unique colors for slices
  .attr('stroke', 'lightgray')
  .attr('stroke-width', 1)
  .on('mouseover', function(event, d) {
    const tooltip = d3.select(tooltipRef.current);
    const [mouseX, mouseY] = d3.pointer(event, svgRef.current);

    tooltip
      .style('opacity', 1)
      .html(`Title: ${d.data.url}<br/>Est. Traffic: ${formatBacklinks(d.data.et)}`)
      .style('left', `${Math.min(mouseX + 10, containerWidth - 120)}px`)
      .style('top', `${Math.min(mouseY - 28, containerHeight - 50)}px`);
  })
  .on('mousemove', function(event) {
    const tooltip = d3.select(tooltipRef.current);
    const [mouseX, mouseY] = d3.pointer(event, svgRef.current);

    tooltip
      .style('left', `${Math.min(mouseX + 10, containerWidth - 120)}px`)
      .style('top', `${Math.min(mouseY - 28, containerHeight - 50)}px`);
  })
  .on('mouseout', function() {
    d3.select(tooltipRef.current)
      .style('opacity', 0);
  });

    // Add legend
    const legend = svg.append('g')
      .attr('transform', `translate(${width - 360},${20})`);

    const legendItemSpacing = 25;

    legend.selectAll('rect')
      .data(formattedData)
      .enter().append('rect')
      .attr('x', 0)
      .attr('y', (d, i) => i * legendItemSpacing)
      .attr('width', 18)
      .attr('height', 18)
      .style('fill', (d, i) => uniqueColors[i]) // Use the same color for legend as for pie chart slice
      .on('mouseover', function(event, d) {
        const legendTooltip = d3.select(legendTooltipRef.current);
        legendTooltip
          .style('opacity', 1)
          .html(d.url)
          .style('left', `${event.clientX + 15}px`)
          .style('top', `${event.clientY + 15}px`);
      })
      .on('mousemove', function(event) {
        const legendTooltip = d3.select(legendTooltipRef.current);
        legendTooltip
          .style('left', `${event.clientX + 15}px`)
          .style('top', `${event.clientY + 15}px`);
      })
      .on('mouseout', function() {
        d3.select(legendTooltipRef.current)
          .style('opacity', 0);
      });

    legend.selectAll('text')
      .data(formattedData)
      .enter().append('text')
      .attr('x', 25)
      .attr('y', (d, i) => i * legendItemSpacing + 15)
      .style('font-size', '12px')
      .style('fill', 'lightgray')
      .style('font-family', 'Quicksand')
      .style('text-anchor', 'start')
      .style('white-space', 'normal')
      .style('word-break', 'break-word')
      .text(d => new URL(d.url).hostname);

  }, [data]);

  return (
    <Container>
      <ChartTitleStyled><PieChartIcon /> SERP Share Based on Estimated Traffic for <span style={{fontFamily: 'Quicksand', color: 'dodgerblue'}}>{keyword}</span> in <span style={{fontFamily: 'Quicksand', color: 'dodgerblue'}}>{location}</span></ChartTitleStyled>
      <ChartContainer>
        <svg ref={svgRef}></svg>
        <Tooltip ref={tooltipRef}></Tooltip>
        <LegendTooltip ref={legendTooltipRef}></LegendTooltip>
      </ChartContainer>
    </Container>
  );
};

export default MarketSharePieChart;
